import * as React from "react";
import { Check, ChevronsUpDown } from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "@/components/ui/command";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";

export interface ComboboxOption {
	value: string;
	label: string;
}

interface ComboboxProps {
	options: ComboboxOption[];
	value?: string;
	onValueChange?: (value: string) => void;
	placeholder?: string;
	searchPlaceholder?: string;
	emptyMessage?: string;
	disabled?: boolean;
	className?: string;
}

export function Combobox({
	options,
	value,
	onValueChange,
	placeholder = "Select an option...",
	searchPlaceholder = "Search...",
	emptyMessage = "No option found.",
	disabled = false,
	className,
}: ComboboxProps) {
	const [open, setOpen] = React.useState(false);
	const [customValue, setCustomValue] = React.useState("");

	// Reset custom value when popover closes
	React.useEffect(() => {
		if (!open) {
			setCustomValue("");
		}
	}, [open]);

	// Get the display value
	const displayValue = React.useMemo(() => {
		if (!value) return placeholder;
		const option = options.find((opt) => opt.value === value);
		return option ? option.label : value;
	}, [value, options, placeholder]);

	const handleSelect = (selectedValue: string) => {
		onValueChange?.(selectedValue);
		setOpen(false);
		setCustomValue("");
	};

	const handleCustomValue = (inputValue: string) => {
		setCustomValue(inputValue);
	};

	const handleCustomSubmit = () => {
		if (customValue.trim()) {
			onValueChange?.(customValue.trim());
			setOpen(false);
			setCustomValue("");
		}
	};

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<Button
					variant="outline"
					role="combobox"
					aria-expanded={open}
					className={cn("w-full justify-between", className)}
					disabled={disabled}
				>
					<span className="truncate">{displayValue}</span>
					<ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
				</Button>
			</PopoverTrigger>
			<PopoverContent className="w-[400px] p-0" align="start">
				<Command shouldFilter={false}>
					<CommandInput
						placeholder={searchPlaceholder}
						value={customValue}
						onValueChange={handleCustomValue}
						onKeyDown={(e) => {
							if (e.key === "Enter" && customValue.trim()) {
								e.preventDefault();
								handleCustomSubmit();
							}
						}}
					/>
					<CommandList>
						<CommandEmpty>
							{customValue.trim() ? (
								<div className="p-2">
									<p className="text-sm text-muted-foreground mb-2">
										{emptyMessage}
									</p>
									<Button
										size="sm"
										className="w-full"
										onClick={handleCustomSubmit}
									>
										Use custom: {customValue.trim()}
									</Button>
								</div>
							) : (
								<p className="text-sm text-muted-foreground">{emptyMessage}</p>
							)}
						</CommandEmpty>
						<CommandGroup>
							{options
								.filter((option) => {
									if (!customValue) return true;
									return (
										option.label
											.toLowerCase()
											.includes(customValue.toLowerCase()) ||
										option.value.toLowerCase().includes(customValue.toLowerCase())
									);
								})
								.map((option) => (
									<CommandItem
										key={option.value}
										value={option.value}
										onSelect={() => handleSelect(option.value)}
									>
										<Check
											className={cn(
												"mr-2 h-4 w-4",
												value === option.value ? "opacity-100" : "opacity-0",
											)}
										/>
										<div className="flex flex-col">
											<span>{option.label}</span>
											{option.label !== option.value && (
												<span className="text-xs text-muted-foreground">
													{option.value}
												</span>
											)}
										</div>
									</CommandItem>
								))}
						</CommandGroup>
					</CommandList>
				</Command>
			</PopoverContent>
		</Popover>
	);
}
